import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Typography,
  IconButton,
  Tooltip as MuiTooltip,
  Box,
  Button,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate, useLocation } from 'react-router-dom';
import { toastError, toastSuccess } from '../../utils/toast';

// Unified wallet context
import { useUnifiedWeb3Wallet, WalletType } from '../../contexts/unified_wallet/UnifiedWeb3WalletContext';

// Fonction pour formater les nombres avec des suffixes (K, M, G, T)
const formatNumber = (num: number): string => {
  if (num >= 1000000000000) return (num / 1000000000000).toFixed(2) + 'T';
  if (num >= 1000000000) return (num / 1000000000).toFixed(2) + 'G';
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
  return num.toString();
};

// Fonction pour formater les tokens
const formatTokens = (tokens: number): string => {
  if (tokens >= 1000000) return (tokens / 1000000).toFixed(2) + 'M';
  if (tokens >= 1000) return (tokens / 1000).toFixed(2) + 'K';
  return tokens.toFixed(0);
};

// Round to nice domain for charts
const roundToNiceDomain = (maxValue: number): number => {
  if (maxValue === 0) return 100;
  if (maxValue < 10) return Math.ceil(maxValue / 2) * 2;
  if (maxValue < 100) return Math.ceil(maxValue / 10) * 10;
  if (maxValue < 1000) return Math.ceil(maxValue / 100) * 100;
  if (maxValue < 10000) return Math.ceil(maxValue / 1000) * 1000;
  return Math.ceil(maxValue / 10000) * 10000;
};

// Generate mock data for charts
const generateMockHistory = (days = 30, maxValue = 100000): { date: string; value: number }[] => {
  const history = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    history.push({
      date: date.toISOString(),
      value: Math.floor(Math.random() * maxValue),
    });
  }
  return history;
};

interface BucketDetails {
  id: string;
  name: string;
  region: string;
  status: string;
  provider: string;
  network: 'stellar' | 'sui';
  tokensIn: number;
  tokensOut: number;
  tokensInHistory: { date: string; value: number }[];
  tokensOutHistory: { date: string; value: number }[];
  latency: number;
  latencyHistory: { date: string; value: number }[];
  calls: number;
  callsHistory: { date: string; value: number }[];
}

// User usage data interface
interface UserUsage {
  userId: string;
  userName: string;
  spent: number;
  storage: number;
  hub: number;
  upload: number;
  download: number;
  change: number;
}

// Format bytes to readable string
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const value = bytes / Math.pow(k, i);
  return `${value.toFixed(2)} ${sizes[i]}`;
};

function DePINHubBucketDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract bucketId from pathname: /stellar-buckets/:id or /sui-buckets/:id
  const pathParts = location.pathname.split('/');
  const bucketId = pathParts[pathParts.length - 1];
  
  const { walletType, isConnected } = useUnifiedWeb3Wallet();

  const network: 'stellar' | 'sui' = walletType === WalletType.STELLAR ? 'stellar' : 'sui';

  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState<BucketDetails | null>(null);
  const [selectedPreset, setSelectedPreset] = useState('Last 30 days');
  const [usersUsage, setUsersUsage] = useState<UserUsage[]>([]);

  // Load bucket details
  useEffect(() => {
    const loadBucketDetails = async () => {
      setLoading(true);
      try {
        // Mock data - in production, this would come from API
        const mockBucket: BucketDetails = {
          id: bucketId || 'unknown',
          name: `DePIN Storage Unit ${bucketId?.slice(0, 8)}`,
          region: 'US East',
          status: 'Active',
          provider: network === 'stellar' ? 'Stellar' : 'SUI',
          network: network,
          tokensIn: Math.floor(Math.random() * 500000) + 100000,
          tokensOut: Math.floor(Math.random() * 300000) + 50000,
          tokensInHistory: generateMockHistory(30, 50000),
          tokensOutHistory: generateMockHistory(30, 30000),
          latency: Math.floor(Math.random() * 100) + 20,
          latencyHistory: generateMockHistory(30, 150),
          calls: Math.floor(Math.random() * 10000) + 1000,
          callsHistory: generateMockHistory(30, 1000),
        };
        setBucket(mockBucket);

        // Mock users usage data
        const mockUsersUsage: UserUsage[] = [
          { userId: 'user-1', userName: 'Alice Johnson', spent: 125.50, storage: 52428800000, hub: 10485760000, upload: 21474836480, download: 10737418240, change: 5.2 },
          { userId: 'user-2', userName: 'Bob Smith', spent: 89.25, storage: 31457280000, hub: 5242880000, upload: 15728640000, download: 8589934592, change: -2.1 },
          { userId: 'user-3', userName: 'Carol White', spent: 210.00, storage: 104857600000, hub: 20971520000, upload: 42949672960, download: 21474836480, change: 12.8 },
          { userId: 'user-4', userName: 'David Lee', spent: 45.75, storage: 10485760000, hub: 2097152000, upload: 5368709120, download: 2684354560, change: -0.5 },
          { userId: 'user-5', userName: 'Eve Martin', spent: 156.80, storage: 73400320000, hub: 15728640000, upload: 32212254720, download: 16106127360, change: 8.3 },
        ];
        setUsersUsage(mockUsersUsage);
      } catch (error) {
        console.error('Error loading bucket details:', error);
        toastError('Failed to load storage unit details');
      } finally {
        setLoading(false);
      }
    };

    if (bucketId) {
      loadBucketDetails();
    }
  }, [bucketId, network]);

  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toastSuccess('Copied to clipboard');
      })
      .catch(() => {
        toastError('Failed to copy to clipboard');
      });
  };

  const getChartDateFormat = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return {
      short: `${month}/${day}`,
      full: `${month}/${day}/${date.getFullYear()}`,
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Typography>Loading storage unit details...</Typography>
      </div>
    );
  }

  if (!bucket) {
    return (
      <div className="flex justify-center items-center h-full">
        <Typography>Storage unit not found</Typography>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-4 pb-6 pt-6 md:pt-8">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Fixed height container for back button to prevent layout shift */}
          <div style={{ minHeight: '48px', display: 'flex', alignItems: 'center' }}>
            <Button
              variant="text"
              startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
              onClick={() => navigate('/depin-hub/storage-units')}
              sx={{ color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
            >
              Back to Storage Units
            </Button>
          </div>

          {/* Header card */}
          <div className="flex justify-center">
            <div className="w-[1080px] h-[85px] flex items-center justify-between px-4 border-b border-[#E9EAEB] mb-6">
              <div className="flex flex-col items-start">
                <Typography
                  sx={{ color: '#101828', fontWeight: 600, fontSize: '20px', lineHeight: '30px' }}
                >
                  {bucket.name}
                </Typography>
                {/* Buttons row under title */}
                <div className="mt-4 flex items-center space-x-2">
                  {/* Short ID button */}
                  <button
                    title="Copy storage unit ID"
                    onClick={() => handleCopy(bucket.id)}
                    style={{
                      height: 22,
                      background: '#FFFFFF',
                      borderRadius: 6,
                      border: '1px solid #D5D7DA',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#414651',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                      cursor: 'pointer',
                    }}
                  >
                    {bucket.id.slice(0, 8)}...
                  </button>

                  {/* Network badge */}
                  <button
                    style={{
                      height: 22,
                      background: '#FFFFFF',
                      borderRadius: 6,
                      border: '1px solid #D5D7DA',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: network === 'stellar' ? '#617BFF' : '#4DA2FF',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {network}
                  </button>

                  {/* Status badge */}
                  <button
                    style={{
                      height: 22,
                      background: '#FFFFFF',
                      borderRadius: 6,
                      border: '1px solid #D5D7DA',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#414651',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: bucket.status === 'Active' ? '#17B26A' : '#F04438',
                        display: 'inline-block',
                        marginRight: 6,
                      }}
                    />
                    {bucket.status}
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Date picker button */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    border: '1px solid #D0D5DD',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    bgcolor: 'white',
                    '&:hover': { bgcolor: '#F9FAFB' },
                    height: '40px',
                  }}
                >
                  <CalendarTodayIcon sx={{ fontSize: 18, color: '#667085' }} />
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#344054' }}>
                    {selectedPreset}
                  </span>
                </Box>

                {/* Reload button */}
                <button
                  onClick={() => window.location.reload()}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    padding: '10px',
                    border: '1px solid #D1D5DB',
                  }}
                  title="Reload data"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Cards grid - 2 columns */}
          <div className="flex justify-center">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ width: '1080px' }}>
              
              {/* Latency card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                <div
                  className="absolute w-full h-full rounded-xl"
                  style={{
                    backgroundColor: '#FDFDFD',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="pt-3 px-4">
                    <Typography
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Latency
                    </Typography>
                  </div>
                </div>

                <div
                  className="absolute bg-white rounded-xl shadow-sm"
                  style={{
                    width: '528px',
                    height: '330px',
                    top: '42px',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-end gap-4 mb-2">
                      <span className="text-sm font-semibold text-gray-900">
                        Avg: {bucket.latency}ms
                      </span>
                    </div>
                    <div style={{ height: 250 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={bucket.latencyHistory.map((point) => {
                            const date = new Date(point.date);
                            const dateFormats = getChartDateFormat(date);
                            return {
                              name: dateFormats.short,
                              value: point.value,
                              fullDate: dateFormats.full,
                            };
                          })}
                          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <linearGradient id="colorLatency" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickFormatter={(value) => `${value}ms`}
                          />
                          <RechartsTooltip
                            formatter={(value: number) => [`${value}ms`, 'Latency']}
                            labelFormatter={(label: any, payload: any) => {
                              if (payload && payload.length > 0 && payload[0]?.payload) {
                                return payload[0].payload.fullDate || label;
                              }
                              return label;
                            }}
                            contentStyle={{
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              backgroundColor: 'white',
                              padding: '8px 12px',
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#F59E0B"
                            fill="url(#colorLatency)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calls card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                <div
                  className="absolute w-full h-full rounded-xl"
                  style={{
                    backgroundColor: '#FDFDFD',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="pt-3 px-4">
                    <Typography
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Requests
                    </Typography>
                  </div>
                </div>

                <div
                  className="absolute bg-white rounded-xl shadow-sm"
                  style={{
                    width: '528px',
                    height: '330px',
                    top: '42px',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-end gap-4 mb-2">
                      <span className="text-sm font-semibold text-gray-900">
                        Total: {formatNumber(bucket.calls)}
                      </span>
                    </div>
                    <div style={{ height: 250 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={bucket.callsHistory.map((point) => {
                            const date = new Date(point.date);
                            const dateFormats = getChartDateFormat(date);
                            return {
                              name: dateFormats.short,
                              value: point.value,
                              fullDate: dateFormats.full,
                            };
                          })}
                          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#788CFF" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#788CFF" stopOpacity={0.1} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickFormatter={(value) => formatNumber(value)}
                          />
                          <RechartsTooltip
                            formatter={(value: number) => [formatNumber(value), 'Calls']}
                            labelFormatter={(label: any, payload: any) => {
                              if (payload && payload.length > 0 && payload[0]?.payload) {
                                return payload[0].payload.fullDate || label;
                              }
                              return label;
                            }}
                            contentStyle={{
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              backgroundColor: 'white',
                              padding: '8px 12px',
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#788CFF"
                            fill="url(#colorCalls)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Token In card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                <div
                  className="absolute w-full h-full rounded-xl"
                  style={{
                    backgroundColor: '#FDFDFD',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="pt-3 px-4">
                    <Typography
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Token In
                    </Typography>
                  </div>
                </div>

                <div
                  className="absolute bg-white rounded-xl shadow-sm"
                  style={{
                    width: '528px',
                    height: '330px',
                    top: '42px',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-end gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        <ArrowDownwardIcon sx={{ fontSize: 14, color: '#10B981' }} />
                        <span className="text-xs text-gray-600">Total</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatTokens(bucket.tokensIn)} tokens
                        </span>
                      </div>
                    </div>
                    <div style={{ height: 250 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={bucket.tokensInHistory.map((point) => {
                            const date = new Date(point.date);
                            const dateFormats = getChartDateFormat(date);
                            return {
                              name: dateFormats.short,
                              value: point.value,
                              fullDate: dateFormats.full,
                            };
                          })}
                          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <linearGradient id="colorTokenIn" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickFormatter={(value) => formatTokens(value)}
                          />
                          <RechartsTooltip
                            formatter={(value: number) => [formatTokens(value), 'Token In']}
                            labelFormatter={(label: any, payload: any) => {
                              if (payload && payload.length > 0 && payload[0]?.payload) {
                                return payload[0].payload.fullDate || label;
                              }
                              return label;
                            }}
                            contentStyle={{
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              backgroundColor: 'white',
                              padding: '8px 12px',
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#10B981"
                            fill="url(#colorTokenIn)"
                            strokeWidth={2}
                            name="Token In"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Token Out card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                <div
                  className="absolute w-full h-full rounded-xl"
                  style={{
                    backgroundColor: '#FDFDFD',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="pt-3 px-4">
                    <Typography
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Token Out
                    </Typography>
                  </div>
                </div>

                <div
                  className="absolute bg-white rounded-xl shadow-sm"
                  style={{
                    width: '528px',
                    height: '330px',
                    top: '42px',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-end gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        <ArrowUpwardIcon sx={{ fontSize: 14, color: '#3B82F6' }} />
                        <span className="text-xs text-gray-600">Total</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatTokens(bucket.tokensOut)} tokens
                        </span>
                      </div>
                    </div>
                    <div style={{ height: 250 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={bucket.tokensOutHistory.map((point) => {
                            const date = new Date(point.date);
                            const dateFormats = getChartDateFormat(date);
                            return {
                              name: dateFormats.short,
                              value: point.value,
                              fullDate: dateFormats.full,
                            };
                          })}
                          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <linearGradient id="colorTokenOut" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickFormatter={(value) => formatTokens(value)}
                          />
                          <RechartsTooltip
                            formatter={(value: number) => [formatTokens(value), 'Token Out']}
                            labelFormatter={(label: any, payload: any) => {
                              if (payload && payload.length > 0 && payload[0]?.payload) {
                                return payload[0].payload.fullDate || label;
                              }
                              return label;
                            }}
                            contentStyle={{
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              backgroundColor: 'white',
                              padding: '8px 12px',
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#3B82F6"
                            fill="url(#colorTokenOut)"
                            strokeWidth={2}
                            name="Token Out"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Users Usage Table */}
          <div className="flex justify-center mt-6">
            <div
              className="bg-white rounded-xl shadow-sm"
              style={{
                width: '1080px',
                border: '1px solid #E9EAEB',
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <div
                className="px-4 py-3"
                style={{
                  backgroundColor: '#FDFDFD',
                  borderBottom: '1px solid #E9EAEB',
                }}
              >
                <Typography
                  style={{
                    color: '#181D27',
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                >
                  Users Usage
                </Typography>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="border-b border-gray-200" style={{ backgroundColor: '#F9FAFB', height: '44px' }}>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Spent
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Storage
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hub
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Upload
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Download
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Change
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usersUsage.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                          No users have used this deal yet
                        </td>
                      </tr>
                    ) : (
                      usersUsage.map((user) => (
                        <tr key={user.userId} className="hover:bg-gray-50 transition-all" style={{ height: '56px' }}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                                style={{ backgroundColor: '#617BFF' }}
                              >
                                {user.userName.split(' ').map(n => n[0]).join('')}
                              </div>
                              <span className="text-sm font-medium text-gray-900">{user.userName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900 font-semibold">${user.spent.toFixed(2)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">{formatBytes(user.storage)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600">{formatBytes(user.hub)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <ArrowUpwardIcon sx={{ fontSize: 14, color: '#10B981' }} />
                              <span className="text-sm text-gray-600">{formatBytes(user.upload)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <ArrowDownwardIcon sx={{ fontSize: 14, color: '#3B82F6' }} />
                              <span className="text-sm text-gray-600">{formatBytes(user.download)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`text-sm font-medium ${user.change >= 0 ? 'text-green-600' : 'text-red-600'}`}
                            >
                              {user.change >= 0 ? '+' : ''}{user.change.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DePINHubBucketDetails;
